<script setup lang="ts">
import { AisRefinementList, AisStateResults } from 'vue-instantsearch/vue3/es'
import type { SearchItem } from 'vue-instantsearch/vue3/es'
import { vOnClickOutside } from '@vueuse/components'
import { getColorSwatch } from '~/utils/colorSwatch'
import type { ColorSwatch, ColorSwatchCache } from '~/types/colorSwatch'

type FilterItem = SearchItem & { id?: string }
type FilterItemWithSwatch = FilterItem & { swatch?: ColorSwatch }

const props = defineProps<{
    attribute: string
    label: string
    isOpen: boolean
    /** 'color' renders a swatch (hex or media) next to each option's label. */
    displayType?: 'text' | 'color'
}>()

const emit = defineEmits<{
    toggle: []
    close: []
}>()

const { locale } = useI18n()
const { public: { colorSwatches } } = useRuntimeConfig()

const searchTerm = ref('')

// Resolved once per item (not per template binding) so the color lookup only runs
// once per render, regardless of how many places an item's swatch is displayed.
function withSwatch(items: FilterItem[]): FilterItemWithSwatch[] {
    if (props.displayType !== 'color') {
        return items
    }

    return items.map(item => ({
        ...item,
        swatch: getColorSwatch(colorSwatches as ColorSwatchCache, locale.value, item.label),
    }))
}

function itemsSelected(items: FilterItem[]): string[] {
    return items.filter(item => item.isRefined).map(item => item.value)
}

function resetFilterGroup(items: FilterItem[], refine: (value: string) => void) {
    itemsSelected(items).forEach(refine)
}

function getFilterOptions(items: FilterItem[]): FilterItem[] {
    return searchTerm.value
        ? items.filter(item => item.label.toLowerCase().includes(searchTerm.value.toLowerCase()))
        : items
}
</script>

<template>
    <ais-refinement-list
        :attribute="attribute"
        :sort-by="['count:desc', 'name:asc']"
        :class-names="{
            'ais-RefinementList--noRefinement': 'hidden'
        }"
    >
        <template #default="{ items, refine }">
            <div v-on-click-outside="() => emit('close')" class="filter-dropdown relative shrink-0">
                <button
                    :id="attribute"
                    type="button"
                    tabindex="0"
                    class="group w-full flex justify-between items-center font-semibold min-h-11 py-3 pl-4 pr-1.5 md:py-0 border-b md:w-auto md:btn md:btn-filter md:gap-1 md:pr-3 md:pl-[14px] bg-base-100"
                    :class="{
                        'md:btn-filter-set': itemsSelected(items).length > 0,
                        'bg-base-200': isOpen
                    }"
                    @click="emit('toggle')"
                >
                    <span class="flex items-center min-h-11 md:block md:min-h-fit">{{ label }}</span>

                    <span class="flex gap-3 items-center md:gap-1">
                        <span v-if="itemsSelected(items).length > 0" class="badge badge-sm badge-dark">{{ itemsSelected(items).length }}</span>
                        <span
                            class="w-11 h-11 rounded-full items-center flex justify-center hover:bg-tertiary-focus md:h-auto md:w-auto md:hover:bg-transparent"
                            :class="{ 'bg-tertiary-focus md:bg-transparent rotate-180' : isOpen}"
                        >
                            <MiscIcon name="chevron-down" />
                        </span>
                    </span>
                </button>

                <div
                    v-show="isOpen"
                    tabindex="0"
                    class="filter-dropdown-content max-h-[400px] xl:max-h-[450px] overflow-y-auto flex flex-col pb-0 bg-base-100 z-20 border-b md:absolute md:shadow md:rounded-box md:border md:w-80 md:translate-y-3"
                >
                    <div v-if="items.length >=10" class="sticky top-0 z-50 bg-base-100 p-4 border-b">
                        <input
                            :id="`inputFilter${label}`"
                            v-model="searchTerm"
                            :placeholder="`${$t('product.listing.filter.input')}`"
                            class="input text-sm w-full pr-10"
                        >
                        <button
                            v-if="searchTerm"
                            type="button"
                            :aria-label="$t('product.listing.filter.reset')"
                            class="absolute right-5 top-1/2 -translate-y-1/2 btn btn-ghost btn-sm"
                            @click="searchTerm = ''"
                        >
                            <MiscIcon name="x" />
                        </button>
                    </div>

                    <div v-if="itemsSelected(items).length > 0" class="flex flex-col p-4 border-b">
                        <span class="text-xs font-semibold">{{ $t('product.listing.filter.select') }}</span>

                        <div class="flex justify-between items-center gap-2 pt-3">
                            <div class="flex flex-wrap gap-3">
                                <span
                                    v-for="selectedItem in withSwatch(items.filter((item: FilterItem) => item.isRefined))"
                                    :key="selectedItem.value"
                                    class="font-semibold rounded-none text-xs min-h-8 px-3 py-2 content-center flex items-center gap-2 border border-base-250 bg-base-250"
                                >
                                    <SearchColorSwatch v-if="selectedItem.swatch" :swatch="selectedItem.swatch" />
                                    <span>{{ selectedItem.label }}</span>
                                    <button
                                        type="button"
                                        :aria-label="$t('product.listing.filter.reset')"
                                        class="transition-opacity cursor-pointer"
                                        @click="refine(selectedItem.value)"
                                        @keydown.space.prevent="refine(selectedItem.value)"
                                    >
                                        <MiscIcon name="x" class="!w-4 !h-4" />
                                    </button>
                                </span>

                                <button
                                    v-if="itemsSelected(items).length >= 2"
                                    type="button"
                                    class="font-normal text-xs underline shrink-0 cursor-pointer"
                                    @click="resetFilterGroup(items, refine)"
                                >
                                    {{ $t('product.listing.filter.reset') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <ul class="flex flex-col flex-wrap gap-3 p-4">
                        <li
                            v-for="(option, i) in withSwatch(getFilterOptions(items))"
                            :key="option.id || `${option.value}_${i}`"
                        >
                            <label
                                :for="option.id || `${option.value}_${i}`"
                                class="cursor-pointer relative label justify-start p-0 flex gap-2"
                                role="button"
                                tabindex="0"
                                @keydown.enter.prevent="refine(option.value)"
                            >
                                <input
                                    :id="option.id || `${option.value}_${i}`"
                                    :value="option.value"
                                    type="checkbox"
                                    :checked="option.isRefined"
                                    class="checkbox rounded-none checkbox-md"
                                    :data-insights-filter="`${attribute}:${option.value}`"
                                    @change="refine(option.value)"
                                >
                                <span class="checkbox-color-label rounded-none flex flex-wrap py-0.5 gap-1.5 z-20 items-center">
                                    <SearchColorSwatch v-if="option.swatch" :swatch="option.swatch" />
                                    <span class="label-text font-semibold">{{ option.label }}</span>
                                </span>
                            </label>
                        </li>
                    </ul>

                    <ais-state-results class="hidden md:block sticky bottom-0 w-full z-50 bg-base-100 p-4 border-t">
                        <template #default="{ nbHits }">
                            <div>
                                <button
                                    type="button"
                                    class="btn btn-secondary w-full"
                                    @click="emit('close')"
                                    @keydown.enter.prevent="emit('close')"
                                >
                                    {{ $t('product.listing.filter.results', { count: nbHits }) }}
                                </button>
                            </div>
                        </template>
                    </ais-state-results>
                </div>
            </div>
        </template>
    </ais-refinement-list>
</template>
